/**
 * useCloudDebts Hook
 * Manages debt data in Cloudflare D1 for authenticated users
 */

import { useState, useCallback, useEffect } from 'react';
import { useUser } from '../contexts/UserContextUnified';
import { analytics } from '../lib/posthog';
import { getToken } from '../utils/tokenStorage';

// API base URL - update this to match your worker deployment
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://your-worker-domain.workers.dev';

export const useCloudDebts = () => {
  const { user } = useUser();
  const [cloudDebts, setCloudDebts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Get auth token from user object
  const getAuthToken = useCallback(() => {
    // Assuming token is stored in user object or localStorage
    const token = getToken() || user?.accessToken;
    if (!token) {
      throw new Error('No authentication token available');
    }
    return token;
  }, [user]);

  // API request helper with authentication
  const apiRequest = useCallback(async (endpoint, options = {}) => {
    try {
      const token = getAuthToken();
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          ...options.headers
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }, [getAuthToken]);

  // Load debts from cloud
  const loadCloudDebts = useCallback(async () => {
    if (!user) {
      setCloudDebts([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await apiRequest('/api/debts');
      setCloudDebts(data.debts || []);
      
      // Track successful load
      analytics.track('cloud_debts_loaded', {
        user_id: user.id,
        debts_count: data.debts?.length || 0,
        timestamp: new Date().toISOString()
      });
      
    } catch (err) {
      setError(err.message);
      console.error('Failed to load cloud debts:', err);
      
      // Track error
      analytics.track('cloud_debts_load_error', {
        user_id: user.id,
        error: err.message,
        timestamp: new Date().toISOString()
      });
      
    } finally {
      setLoading(false);
    }
  }, [user, apiRequest]);

  // Load debts when user changes
  useEffect(() => {
    loadCloudDebts();
  }, [loadCloudDebts]);

  // Add debt to cloud
  const addCloudDebt = useCallback(async (debtData) => {
    if (!user) throw new Error('User not authenticated');

    try {
      const response = await apiRequest('/api/debts', {
        method: 'POST',
        body: JSON.stringify(debtData)
      });

      const newDebt = response.debt;
      setCloudDebts(prev => [...prev, newDebt]);

      // Track debt addition
      analytics.track('cloud_debt_added', {
        user_id: user.id,
        debt_name: newDebt.name,
        debt_balance: newDebt.balance,
        timestamp: new Date().toISOString()
      });

      return newDebt;
      
    } catch (err) {
      setError(err.message);
      console.error('Failed to add cloud debt:', err);
      throw err;
    }
  }, [user, apiRequest]);

  // Update debt in cloud
  const updateCloudDebt = useCallback(async (debtId, updates) => {
    if (!user) throw new Error('User not authenticated');

    try {
      const response = await apiRequest(`/api/debts/${debtId}`, {
        method: 'PUT',
        body: JSON.stringify(updates)
      });

      setCloudDebts(prev => 
        prev.map(debt => 
          debt.id === debtId 
            ? { ...debt, ...updates, updatedAt: response.updated_at }
            : debt
        )
      );

      // Track debt update
      analytics.track('cloud_debt_updated', {
        user_id: user.id,
        debt_id: debtId,
        fields_updated: Object.keys(updates),
        timestamp: new Date().toISOString()
      });

      return { ...updates, updatedAt: response.updated_at };
      
    } catch (err) {
      setError(err.message);
      console.error('Failed to update cloud debt:', err);
      throw err;
    }
  }, [user, apiRequest]);

  // Delete debt from cloud
  const deleteCloudDebt = useCallback(async (debtId) => {
    if (!user) throw new Error('User not authenticated');

    try {
      await apiRequest(`/api/debts/${debtId}`, {
        method: 'DELETE'
      });

      const deletedDebt = cloudDebts.find(debt => debt.id === debtId);
      setCloudDebts(prev => prev.filter(debt => debt.id !== debtId));

      // Track debt deletion
      analytics.track('cloud_debt_deleted', {
        user_id: user.id,
        debt_id: debtId,
        debt_name: deletedDebt?.name,
        remaining_debts: cloudDebts.length - 1,
        timestamp: new Date().toISOString()
      });

    } catch (err) {
      setError(err.message);
      console.error('Failed to delete cloud debt:', err);
      throw err;
    }
  }, [user, apiRequest, cloudDebts]);

  // Reorder debts in cloud
  const reorderCloudDebts = useCallback(async (newOrder) => {
    if (!user) throw new Error('User not authenticated');

    try {
      // Update all debts with new order
      const updates = newOrder.map((debt, index) => 
        updateCloudDebt(debt.id, { order: index + 1 })
      );

      await Promise.all(updates);

      // Update local state
      const reorderedDebts = newOrder.map((debt, index) => ({
        ...debt,
        order: index + 1
      }));
      
      setCloudDebts(reorderedDebts);

      // Track reordering
      analytics.track('cloud_debts_reordered', {
        user_id: user.id,
        debts_count: reorderedDebts.length,
        new_order: reorderedDebts.map(d => ({ id: d.id, name: d.name, order: d.order })),
        timestamp: new Date().toISOString()
      });

    } catch (err) {
      setError(err.message);
      console.error('Failed to reorder cloud debts:', err);
      throw err;
    }
  }, [user, updateCloudDebt]);

  // Clear all data (for logout)
  const clearCloudData = useCallback(() => {
    setCloudDebts([]);
    setError(null);
    setLoading(false);
  }, []);

  // Calculate totals
  const totalCloudBalance = cloudDebts.reduce((sum, debt) => sum + (debt.balance || 0), 0);
  const totalMinPayments = cloudDebts.reduce((sum, debt) => sum + (debt.minPayment || 0), 0);

  return {
    // Data
    debts: cloudDebts,
    loading,
    error,
    
    // Actions
    addDebt: addCloudDebt,
    updateDebt: updateCloudDebt,
    deleteDebt: deleteCloudDebt,
    saveDebt: updateCloudDebt, // Alias for compatibility
    reorderDebts: reorderCloudDebts,
    
    // Utilities
    loadDemoData: () => {}, // No-op for cloud mode
    clearAllData: clearCloudData,
    refresh: loadCloudDebts,
    
    // Computed values
    totalBalance: totalCloudBalance,
    totalMinPayments,
    
    // Cloud mode flag
    isCloudMode: true
  };
};

export default useCloudDebts;